import { useAtomValue } from 'jotai'
import {
  GreyGrey30,
  ShadowXs,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '@sequencehq/core-components'
import { formatDecimal } from '@sequencehq/utils'

import {
  invoiceIdAtom,
  invoiceSubAccountUsageAtom,
  lineItemGroupsAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { Section } from '../../components'
import { useLineItemGroupsUsageData } from './useLoadUsageForLineItemGroups'

const SECTION_TITLE = 'Usage'

export const Usage = () => {
  const subAccountUsage = useAtomValue(invoiceSubAccountUsageAtom)

  if (subAccountUsage?.length) {
    return <SubAccountUsage />
  }

  return <LineItemGroupUsage />
}

const SubAccountUsage = () => {
  const subAccountUsage = useAtomValue(invoiceSubAccountUsageAtom)

  return (
    <Section
      title={SECTION_TITLE}
      data-testid="invoice.usage.sub-account-usage"
    >
      <TableContainer
        borderRadius="lg"
        overflow="hidden"
        border={`1px solid ${GreyGrey30}`}
        width="100%"
        boxShadow={ShadowXs}
      >
        <Table variant="v2" width="100%">
          <Thead userSelect="none">
            <Tr>
              <Th>Sub Account</Th>
              <Th>Metric name</Th>
              <Th textAlign="right">Value</Th>
            </Tr>
          </Thead>
          <Tbody>
            {subAccountUsage?.flatMap(subAccount =>
              subAccount.usageData.map((usageData, subAccountIdx) => (
                <Tr
                  key={`${subAccount.subAccountLabel}-${usageData.label}`}
                  cursor="default !important"
                >
                  <Td>{subAccountIdx === 0 && subAccount.subAccountLabel}</Td>
                  <Td>{usageData.label}</Td>
                  <Td textAlign="right">
                    {formatDecimal()(usageData.quantity)}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Section>
  )
}

const LineItemGroupUsage = () => {
  const invoiceId = useAtomValue(invoiceIdAtom)
  const lineItemGroups = useAtomValue(lineItemGroupsAtom)
  const lineItemGroupIds = lineItemGroups?.map(li => li.id) ?? []

  const { data: usageData, isLoading } = useLineItemGroupsUsageData({
    invoiceId: invoiceId ?? '',
    lineItemGroupIds
  })

  if (isLoading || typeof usageData === 'undefined') {
    return (
      <Section title={SECTION_TITLE}>
        <Skeleton height="200px" width="100%" />
      </Section>
    )
  }

  if (usageData.length === 0) return null

  return (
    <Section
      title={SECTION_TITLE}
      data-testid="invoice.usage.line-item-group-usage"
    >
      <TableContainer
        borderRadius="lg"
        overflow="hidden"
        border={`1px solid ${GreyGrey30}`}
        width="100%"
        boxShadow={ShadowXs}
      >
        <Table variant="v2" width="100%">
          <Thead userSelect="none">
            <Tr>
              <Th>Metric name</Th>
              <Th textAlign="right">Value</Th>
            </Tr>
          </Thead>
          <Tbody>
            {usageData.map(({ metricId, metricName, total }) => (
              <Tr key={metricId} cursor="default !important">
                <Td>{metricName}</Td>
                <Td textAlign="right">{formatDecimal()(total)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Section>
  )
}
